import React, { useEffect, useRef, useState } from "react";
import "./BusinessSettings.css";
import { getDuplicateMode, saveDuplicateMode } from "../../Services/DuplicateService/DuplicateApi";
import AlertMessage from "../../Components/AlertMessage/AlertMessage";
import { useSelector } from "react-redux";

type SettingsSection = "duplicateItems" | null;

type HandlingMode = 0 | 1 | 2;

const MODE_OPTIONS: { value: HandlingMode; label: string; help: string }[] = [
  { value: 0, label: "Off", help: "Duplicate invoices are not checked." },
  { value: 1, label: "Manual review", help: "Possible duplicates are flagged so you can review each item in Submission History." },
  { value: 2, label: "Automatically block", help: "Possible duplicates are blocked automatically. You can review the blocked item in Submission History." },
];

const BusinessSettings: React.FC = () => {
  const [selectedSection, setSelectedSection] =
    useState<SettingsSection>("duplicateItems");

  const [open, setOpen] = useState(false);
  const [Client, setClient] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<HandlingMode | null>(null);
const [saving, setSaving] = useState(false);
const [loadError, setLoadError] = useState(false);

  const selectedClient = useSelector((state: any) => state.selectedClient);

 useEffect(() => {
  getDuplicateMode()
    .then((m) => (m === 0 || m === 1 || m === 2 ? setMode(m) : setLoadError(true)))
    .catch(() => setLoadError(true));
}, []);

const handleModeChange = async (newMode: HandlingMode) => {
  setOpen(false);
  if (mode === null || saving || newMode === mode) return;

  const previous = mode;
  setMode(newMode);
  setSaving(true);
  try {
    await saveDuplicateMode(newMode);
    AlertMessage({
      type: "success",
      text:
        newMode === 1
          ? "Saved. Existing invoices are being rescanned; flags will appear shortly."
          : newMode === 0
          ? "Saved. Existing duplicate flags are being cleared."
          : "Saved. New uploads that match an existing invoice will be blocked.",
    });
  } catch {
    setMode(previous);
    AlertMessage({ type: "error", text: "Something went wrong. Please try again." });
  } finally {
    setSaving(false);
  }
};

const current = mode === null ? null : MODE_OPTIONS.find((o) => o.value === mode)!;

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);


  

  return (
    <div className="business-settings">
      {/* Left Sidebar */}
      <aside className="business-settings-sidebar">
        <div className="settings-title">Settings</div>
        <button
          type="button"
          className={`duplicate-items-btn ${
            selectedSection === "duplicateItems" ? "selected" : ""
          }`}
          onClick={() => setSelectedSection("duplicateItems")}
        >
          Duplicate Items
        </button>
      </aside>

      {/* Main Content */}
      <main className="business-settings-content">
        {selectedSection === "duplicateItems" && (
          <div className="duplicate-card">
            <div className="duplicate-header">
              <svg className="duplicate-icon" viewBox="0 0 32 32" fill="none">
                <rect x="3" y="3" width="15" height="19" rx="2.5" fill="#fff" stroke="#1f2d5c" strokeWidth="2.2" />
                <rect x="13" y="10" width="15" height="19" rx="2.5" fill="#fff" stroke="#f28c1e" strokeWidth="2.2" />
              </svg>
              <div className="duplicate-text">
                <h2>Duplicate Detection</h2>
                <p>Choose how possible duplicate invoices are handled after extraction.</p>
              </div>
            </div>

            <label className="handling-label">Handling mode</label>

            <div className="handling-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className={`handling-select ${open ? "open" : ""}`}
                disabled={mode === null || saving}
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
              >
                <span>{loadError ? "Unavailable" : current?.label ?? "Loading…"}</span>
                <svg className={`chevron ${open ? "up" : ""}`} width="12" height="12" viewBox="0 0 12 12">
                  <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {open && (
                <ul className="handling-menu" role="listbox">
                  {MODE_OPTIONS.map((opt) => (
                    <li
                      key={opt.value}
                      role="option"
                      aria-selected={opt.value === mode}
                      className={`handling-option ${opt.value === mode ? "selected" : ""}`}
                      onClick={() => handleModeChange(opt.value)}
                    >
                      <span>{opt.label}</span>
                      {opt.value === mode && (
                        <svg width="14" height="14" viewBox="0 0 14 14">
                          <path d="M2 7.5l3.5 3.5L12 3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {current && <p className="handling-help">{current.help}</p>}

            <div className="duplicate-footer">
              Changes apply to newly processed invoices. Existing flagged
              invoices remain available for review.
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BusinessSettings;